import { driver, type DriveStep, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './productTourOverrides.css';

/* Anchor selectors in the app DOM. */
const TOUR_BUTTON_SELECTOR = '.app-navbar__tour-btn';
const MAIN_CONTENT_SELECTOR = '[data-tour="main-content"]';
const DOMAINS_NAV_SELECTOR = '[data-tour="domains-nav"]';
const ADD_DOMAIN_SELECTOR = '[data-tour="add-context-btn"]';
const DOMAIN_LIST_SELECTOR = '[data-tour="contexts-list"]';

/**
 * Selectors whose targets live inside the collapsible "Domains" secondary panel.
 * The panel is closed on the landing view where the tour starts, so these steps
 * must open it first (see openDomainsPanelIfNeeded / onHighlightStarted).
 */
const PANEL_SELECTORS = new Set<string>([ADD_DOMAIN_SELECTOR, DOMAIN_LIST_SELECTOR]);

/** S2-style close glyph injected into driver's imperative close button. */
const CLOSE_ICON_SVG =
  '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false">' +
  '<path fill="currentColor" d="M10 8.586 15.293 3.293a1 1 0 1 1 1.414 1.414L11.414 10l5.293 ' +
  '5.293a1 1 0 0 1-1.414 1.414L10 11.414l-5.293 5.293a1 1 0 0 1-1.414-1.414L8.586 10 3.293 ' +
  '4.707a1 1 0 0 1 1.414-1.414L10 8.586Z"/></svg>';

export const TOUR_STEPS: DriveStep[] = [
  {
    element: TOUR_BUTTON_SELECTOR,
    popover: {
      title: 'Welcome to Guides Enhanced Answers',
      description:
        'This short tour will show you the main features. You can start it anytime from the Product tour button in the navbar.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: DOMAINS_NAV_SELECTOR,
    popover: {
      title: 'Open your domains',
      description:
        'Select Domains in the sidebar to see every domain in your organization. Each domain has its own knowledge graph.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: ADD_DOMAIN_SELECTOR,
    popover: {
      title: 'Create a new domain',
      description:
        'Use Add to create a new domain. You will configure the LLM and embedding models for that domain.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: DOMAIN_LIST_SELECTOR,
    popover: {
      title: 'Enter a domain',
      description:
        'Click a domain to open it. Inside you can start a chat, add content, view indexed content, edit settings, or delete it. Use the back arrow to return here.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: MAIN_CONTENT_SELECTOR,
    popover: {
      title: 'Main area',
      description:
        'Once you are inside a domain, this area shows your chat, forms, or content panels depending on the action you pick from the sidebar.',
      side: 'left',
      align: 'center',
    },
  },
];

let driverInstance: Driver | null = null;
/** True only when the tour itself opened the Domains panel, so we can restore state. */
let domainsPanelOpenedByTour = false;

function isElementPresent(selector: string): boolean {
  return typeof document !== 'undefined' && document.querySelector(selector) !== null;
}

/** Click the Domains nav row, which toggles the secondary panel open/closed. */
function clickDomainsNav(): void {
  document.querySelector<HTMLElement>(DOMAINS_NAV_SELECTOR)?.click();
}

/** Resolve once the selector is in the DOM (panel finished rendering) or we time out. */
function waitForElement(selector: string, timeoutMs = 1500): Promise<boolean> {
  return new Promise((resolve) => {
    if (isElementPresent(selector)) {
      resolve(true);
      return;
    }
    const start = performance.now();
    const tick = (): void => {
      if (isElementPresent(selector)) {
        resolve(true);
      } else if (performance.now() - start > timeoutMs) {
        resolve(false);
      } else {
        requestAnimationFrame(tick);
      }
    };
    requestAnimationFrame(tick);
  });
}

/** Close the Domains panel again if (and only if) the tour opened it. */
function restoreDomainsPanel(): void {
  if (domainsPanelOpenedByTour && isElementPresent(DOMAIN_LIST_SELECTOR)) {
    clickDomainsNav();
  }
  domainsPanelOpenedByTour = false;
}

/**
 * Starts the product tour. Call when the user clicks the Product tour button.
 * Only run when authenticated and on the landing view so all anchors exist.
 * Steps that live inside the collapsible Domains panel auto-open it and then
 * re-highlight once the target renders; the panel is restored when the tour ends.
 */
export function startProductTour(): void {
  if (typeof document === 'undefined') return;

  if (driverInstance?.isActive()) {
    driverInstance.destroy();
  }
  domainsPanelOpenedByTour = false;

  driverInstance = driver({
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    progressText: '{{current}} of {{total}}',
    nextBtnText: 'Next',
    prevBtnText: 'Previous',
    doneBtnText: 'Done',
    // Keep the same dimmed spotlight treatment for every step.
    overlayColor: '#000000',
    overlayOpacity: 0.5,
    steps: TOUR_STEPS,
    onHighlightStarted: (_element: Element | undefined, step: DriveStep) => {
      const selector = typeof step.element === 'string' ? step.element : null;
      if (!selector || !PANEL_SELECTORS.has(selector) || isElementPresent(selector)) {
        return;
      }
      if (!isElementPresent(DOMAIN_LIST_SELECTOR) && !isElementPresent(ADD_DOMAIN_SELECTOR)) {
        clickDomainsNav();
        domainsPanelOpenedByTour = true;
      }
      void waitForElement(selector).then((found) => {
        const index = driverInstance?.getActiveIndex();
        if (found && typeof index === 'number' && driverInstance?.isActive()) {
          // Re-run highlight now that the target exists so it isn't centered.
          driverInstance.moveTo(index);
        }
      });
    },
    onPopoverRender: (popover) => {
      const closeButton = popover.closeButton;
      if (closeButton) {
        closeButton.setAttribute('aria-label', 'Close tour');
        closeButton.innerHTML = CLOSE_ICON_SVG;
      }
    },
    onDestroyed: () => {
      restoreDomainsPanel();
      driverInstance = null;
    },
  });

  driverInstance.drive();
}
